import React, { useState } from 'react';
import { ChevronDown, SlidersHorizontal, X, Check, ChevronLeft, ChevronRight } from 'lucide-react';
interface LedgerEntry {
  ledger_id: string;
  timestamp: string;
  category: string;
  asset: string;
  amount: number;
  fee: number;
  balance_after: number;
}
const mockLedgerData: LedgerEntry[] = [{
  ledger_id: 'LIKYYV',
  timestamp: '1/18/26 4:43 PM',
  category: 'Margin trade',
  asset: 'US Dollar',
  amount: -0.589,
  fee: 0.3144,
  balance_after: 6.3091
}, {
  ledger_id: 'L3YZ2V',
  timestamp: '1/18/26 3:35 PM',
  category: 'Margin trade',
  asset: 'US Dollar',
  amount: 0,
  fee: 0.0396,
  balance_after: 7.2125
}, {
  ledger_id: 'LL3CRC',
  timestamp: '1/18/26 11:35 AM',
  category: 'Margin trade',
  asset: 'US Dollar',
  amount: 0,
  fee: 0.0396,
  balance_after: 7.2521
}, {
  ledger_id: 'LWDGLB',
  timestamp: '1/18/26 7:35 AM',
  category: 'Margin trade',
  asset: 'US Dollar',
  amount: 0,
  fee: 0.0396,
  balance_after: 7.2917
}, {
  ledger_id: 'LAEQIV',
  timestamp: '1/18/26 3:35 AM',
  category: 'Margin trade',
  asset: 'US Dollar',
  amount: 0,
  fee: 0.0396,
  balance_after: 7.3313
}, {
  ledger_id: 'DEP001',
  timestamp: '1/17/26 10:00 AM',
  category: 'Deposit',
  asset: 'Bitcoin',
  amount: 0.5,
  fee: 0,
  balance_after: 0.5
}, {
  ledger_id: 'WDR001',
  timestamp: '1/16/26 2:15 PM',
  category: 'Withdrawal',
  asset: 'Ethereum',
  amount: -2.5,
  fee: 0.005,
  balance_after: 10.2
}, {
  ledger_id: 'REW001',
  timestamp: '1/15/26 9:00 AM',
  category: 'Rewards',
  asset: 'Polkadot',
  amount: 1.2,
  fee: 0,
  balance_after: 150.5
}, {
  ledger_id: 'TRD001',
  timestamp: '1/14/26 11:30 AM',
  category: 'Spot trade',
  asset: 'US Dollar',
  amount: -5000,
  fee: 10,
  balance_after: 2331.3
}, {
  ledger_id: 'TRD002',
  timestamp: '1/14/26 11:30 AM',
  category: 'Spot trade',
  asset: 'Bitcoin',
  amount: 0.12,
  fee: 0,
  balance_after: 0.62
}];
const categories = ['Deposit', 'Withdrawal', 'Transfer', 'Spot trade', 'Margin trade', 'Margin rollover', 'Margin settle', 'Earn', 'Rewards', 'Reward bonus', 'Instant', 'NFT', 'Credit', 'Other'];
const assets = ['USD', 'USDT', 'BTC', 'ETH', 'SOL', 'BNB', 'DOT'];
export function LedgerTab() {
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showAssetFilter, setShowAssetFilter] = useState(false);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const clearFilters = () => {
    setSelectedAssets([]);
    setSelectedCategories([]);
    setDateFrom('');
    setDateTo('');
  };
  const toggleCategory = (category: string) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(c => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };
  const toggleAsset = (asset: string) => {
    if (selectedAssets.includes(asset)) {
      setSelectedAssets(selectedAssets.filter(a => a !== asset));
    } else {
      setSelectedAssets([...selectedAssets, asset]);
    }
  };
  const selectAllCategories = () => setSelectedCategories([...categories]);
  const clearAllCategories = () => setSelectedCategories([]);
  const hasActiveFilters = selectedAssets.length > 0 || selectedCategories.length > 0 || dateFrom || dateTo;
  // Filter data
  const filteredData = mockLedgerData.filter(entry => {
    if (selectedAssets.length > 0) {
      // Simple check for asset name/symbol match
      const assetMatch = selectedAssets.some(a => entry.asset.toLowerCase().includes(a.toLowerCase()) || a === 'USD' && entry.asset === 'US Dollar');
      if (!assetMatch) return false;
    }
    if (selectedCategories.length > 0 && !selectedCategories.includes(entry.category)) {
      return false;
    }
    // Date filtering would go here
    return true;
  });
  const totalItems = filteredData.length;
  const itemsPerPage = 25;
  const currentPage = 1;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  return <div className="space-y-4" data-id="element-297">
      {/* Filters */}
      <div className="glass-border rounded-[20px] p-4" data-id="element-298">
        <div className="flex items-center gap-3 flex-wrap" data-id="element-299">
          {/* Assets Filter */}
          <div className="relative" data-id="element-300">
            <button onClick={() => setShowAssetFilter(!showAssetFilter)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${showAssetFilter ? 'bg-background-elevated text-text-primary ring-1 ring-brand-purple' : 'bg-background-elevated hover:bg-background-elevated/80 text-text-primary'}`} data-id="element-301">
              Assets
              {selectedAssets.length > 0 && <span className="bg-brand-purple text-white text-[10px] px-1.5 py-0.5 rounded-full" data-id="element-302">
                  {selectedAssets.length}
                </span>}
              <ChevronDown size={16} data-id="element-303" />
            </button>
            {showAssetFilter && <>
                <div className="fixed inset-0 z-20" onClick={() => setShowAssetFilter(false)} data-id="element-304" />
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-elevated border border-border-subtle rounded-xl shadow-2xl z-30 p-3 max-h-80 overflow-y-auto" data-id="element-305">
                  <div className="space-y-1" data-id="element-306">
                    {assets.map(asset => <button key={asset} onClick={() => toggleAsset(asset)} className="w-full flex items-center gap-3 p-2 hover:bg-background-secondary rounded-lg transition-colors text-left group" data-id="element-307">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedAssets.includes(asset) ? 'bg-brand-purple border-brand-purple' : 'border-text-secondary group-hover:border-text-primary'}`} data-id="element-308">
                          {selectedAssets.includes(asset) && <Check size={10} className="text-white" data-id="element-309" />}
                        </div>
                        <span className="text-sm text-text-primary" data-id="element-310">
                          {asset}
                        </span>
                      </button>)}
                  </div>
                </div>
              </>}
          </div>

          {/* Categories Filter */}
          <div className="relative" data-id="element-311">
            <button onClick={() => setShowCategoryFilter(!showCategoryFilter)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${showCategoryFilter ? 'bg-background-elevated text-text-primary ring-1 ring-brand-purple' : 'bg-background-elevated hover:bg-background-elevated/80 text-text-primary'}`} data-id="element-312">
              Categories
              {selectedCategories.length > 0 && <span className="bg-brand-purple text-white text-[10px] px-1.5 py-0.5 rounded-full" data-id="element-313">
                  {selectedCategories.length}
                </span>}
              <ChevronDown size={16} data-id="element-314" />
            </button>
            {showCategoryFilter && <>
                <div className="fixed inset-0 z-20" onClick={() => setShowCategoryFilter(false)} data-id="element-315" />
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-elevated border border-border-subtle rounded-xl shadow-2xl z-30 flex flex-col max-h-96" data-id="element-316">
                  <div className="p-3 border-b border-border-subtle flex gap-2" data-id="element-317">
                    <button onClick={selectAllCategories} className="text-xs text-brand-purple-lighter hover:text-white transition-colors font-medium" data-id="element-318">
                      Select All
                    </button>
                    <span className="text-border-subtle" data-id="element-319">|</span>
                    <button onClick={clearAllCategories} className="text-xs text-text-secondary hover:text-text-primary font-medium" data-id="element-320">
                      Clear All
                    </button>
                  </div>
                  <div className="p-3 overflow-y-auto space-y-1" data-id="element-321">
                    {categories.map(category => <button key={category} onClick={() => toggleCategory(category)} className="w-full flex items-center gap-3 p-2 hover:bg-background-secondary rounded-lg transition-colors text-left group" data-id="element-322">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedCategories.includes(category) ? 'bg-brand-purple border-brand-purple' : 'border-text-secondary group-hover:border-text-primary'}`} data-id="element-323">
                          {selectedCategories.includes(category) && <Check size={10} className="text-white" data-id="element-324" />}
                        </div>
                        <span className="text-sm text-text-primary" data-id="element-325">
                          {category}
                        </span>
                      </button>)}
                  </div>
                </div>
              </>}
          </div>

          {/* Date Range */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto" data-id="element-326">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="From" className="px-4 py-2 min-h-[44px] w-full sm:w-auto bg-background-elevated text-text-primary rounded-lg text-sm border border-border-subtle focus:border-border-default outline-none" data-id="element-327" />
            <span className="text-text-secondary hidden sm:inline" data-id="element-328">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="To" className="px-4 py-2 min-h-[44px] w-full sm:w-auto bg-background-elevated text-text-primary rounded-lg text-sm border border-border-subtle focus:border-border-default outline-none" data-id="element-329" />
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-2 px-4 py-2 text-text-secondary hover:text-text-primary transition-colors text-sm" data-id="element-330">
              Clear filters
            </button>}
        </div>
      </div>

      {/* Table */}
      <div className="glass-border rounded-[20px] overflow-hidden" data-id="element-331">
        <div className="overflow-x-auto" data-id="element-332">
          <table className="w-full min-w-[700px]" data-id="element-333">
            <thead className="bg-background-elevated/50" data-id="element-334">
              <tr className="border-b border-border-subtle" data-id="element-335">
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-336">
                  Date
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-337">
                  Type
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-338">
                  Asset
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-339">
                  Amount
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-340">
                  Fee
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-341">
                  Balance
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-342">
                  ID
                </th>
              </tr>
            </thead>
            <tbody data-id="element-343">
              {filteredData.map(entry => <tr key={entry.ledger_id} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors" data-id="element-344">
                  <td className="px-6 py-4 text-sm text-text-primary" data-id="element-345">
                    {entry.timestamp}
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary" data-id="element-346">
                    {entry.category}
                  </td>
                  <td className="px-6 py-4" data-id="element-347">
                    <div className="text-sm font-medium text-text-primary" data-id="element-348">
                      {entry.asset}
                    </div>
                    <div className="text-xs text-text-secondary" data-id="element-349">USD</div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-350">
                    <div className={`font-numbers text-sm ${entry.amount < 0 ? 'text-danger' : entry.amount > 0 ? 'text-success' : 'text-text-primary'}`} data-id="element-351">
                      {entry.amount.toFixed(4)}
                      <span className="text-text-secondary text-xs ml-1" data-id="element-352">
                        USD
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-353">
                    <div className="font-numbers text-sm text-text-primary" data-id="element-354">
                      {entry.fee.toFixed(4)}
                      <span className="text-text-secondary text-xs ml-1" data-id="element-355">
                        USD
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-356">
                    <div className="font-numbers text-sm text-text-primary" data-id="element-357">
                      {entry.balance_after.toFixed(4)}
                      <span className="text-text-secondary text-xs ml-1" data-id="element-358">
                        USD
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-359">
                    <div className="font-numbers text-xs text-text-secondary" data-id="element-360">
                      {entry.ledger_id}
                    </div>
                  </td>
                </tr>)}
              {filteredData.length === 0 && <tr data-id="element-361">
                  <td colSpan={7} className="px-6 py-8 text-center text-text-secondary" data-id="element-362">
                    No ledger entries found matching your filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-border-subtle flex items-center justify-between" data-id="element-363">
          <div className="text-sm text-text-secondary" data-id="element-364">
            {totalItems > 0 ? `1 - ${Math.min(itemsPerPage, totalItems)} of ${totalItems}` : '0 of 0'}
          </div>
          <div className="flex items-center gap-2" data-id="element-365">
            <button disabled={currentPage === 1} className="p-2 text-text-secondary hover:text-text-primary hover:bg-background-elevated rounded-lg transition-all disabled:opacity-50 disabled:hover:bg-transparent" data-id="element-366">
              <ChevronLeft size={16} data-id="element-367" />
            </button>
            <div className="text-sm font-medium text-text-primary px-2" data-id="element-368">
              Page {currentPage} of {totalPages}
            </div>
            <button disabled={currentPage === totalPages} className="p-2 text-text-secondary hover:text-text-primary hover:bg-background-elevated rounded-lg transition-all disabled:opacity-50 disabled:hover:bg-transparent" data-id="element-369">
              <ChevronRight size={16} data-id="element-370" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}